import React from 'react';
import styled, { css } from 'styled-components';

interface DataUnavailableProps {
  /** When true, renders children greyed-out with overlay message */
  unavailable: boolean;
  /** Optional message explaining why data is missing */
  message?: string;
  /** Render the child structure as a skeleton (greyed, no interaction) */
  children: React.ReactNode;
}

/**
 * Wrapper component that greys-out any child component tree when the
 * backing API endpoint doesn't (yet) provide the data.
 *
 * Usage:
 *   <DataUnavailable unavailable={!hasEndpoint} message="Requires /api/v2/alerts">
 *     <AlertBanner title="..." description="..." />
 *   </DataUnavailable>
 *
 * When unavailable=true:
 * - Children are rendered but greyed out (opacity + grayscale)
 * - Pointer events are disabled
 * - A small overlay label indicates the data gap
 *
 * When unavailable=false:
 * - Children render normally with no overhead
 */
export const DataUnavailable: React.FC<DataUnavailableProps> = ({
  unavailable,
  message = 'Data not yet available',
  children
}) => {
  if (!unavailable) {
    return <>{children}</>;
  }

  return (
    <Wrap>
      <Greyed>{children}</Greyed>
      <Overlay>
        <OverlayIcon>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </OverlayIcon>
        <OverlayText>{message}</OverlayText>
      </Overlay>
    </Wrap>
  );
};

const Wrap = styled.div`
  position: relative;
`;

const Greyed = styled.div`
  opacity: 0.35;
  filter: grayscale(0.8);
  pointer-events: none;
  user-select: none;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  pointer-events: none;
`;

const OverlayIcon = styled.span`
  color: var(--ink-faint);
  display: flex;
`;

const OverlayText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted);
  background: var(--white);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
`;
